import { Zap, ListChecks, ClipboardList, AlertCircle, TrendingUp, TrendingDown } from "lucide-react";
import { Block, Tag, List } from "@/components/analysis/resultUi";

const LABELS = {
  how: "Come", when: "Quando", frequency: "Frequenza", avoid: "Cosa non fare", check_after: "Cosa controllare dopo", goal: "Obiettivo",
  where: "Dove osservare", signs: "Cosa cercare", limits: "Limiti", target: "Contro", role: "Ruolo", what_to_do: "Cosa fare",
  conditions: "Condizioni importanti", incompatibilities: "Incompatibilità", expected: "Evoluzione attesa",
  trigger: "Quando intervenire di nuovo", why: "Perché",
};
const ORIGIN = { spontaneo: "Spontaneo · da conservare", introducibile: "Introducibile", entrambi: "Spontaneo e introducibile" };
const MGMT = {
  agronomico: { label: "Intervento agronomico", cls: "bg-[#1E4028] text-white" },
  prevenzione: { label: "Prevenzione e monitoraggio", cls: "bg-[#0369A1] text-white" },
  fisico_meccanico: { label: "Controllo fisico/meccanico e mezzi tecnici", cls: "bg-[#B45309] text-white" },
  biologico: { label: "Controllo biologico", cls: "bg-[#15803D] text-white" },
};
const CORE = Object.keys(MGMT);

export function Fields({ item, title, skip = [], testid }) {
  const rows = Object.entries(item).filter(([k, v]) => v && LABELS[k] && !skip.includes(k));
  return (
    <div className="rounded-xl border border-[#ddc69c]/60 bg-[#fbf9f4] p-3" data-testid={testid}>
      {title}
      {rows.map(([k, v]) => (
        <p key={k} className="mt-1 text-sm text-[#2D3A30]"><span className="font-semibold text-[#5b6b60]">{LABELS[k]}:</span> {v}</p>
      ))}
    </div>
  );
}

const ActionList = ({ items, prefix }) => (
  <div className="space-y-2">
    {items.map((a, i) => (
      <Fields key={i} item={a} testid={`${prefix}-${i}`} title={a.what && <p className="text-sm font-semibold text-[#0D2517]">{a.what}</p>} />
    ))}
  </div>
);

const Sub = ({ icon: Icon, children }) => (
  <p className="mb-2 mt-5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5b6b60]">{Icon && <Icon size={12} />}{children}</p>
);

export const legacyDoNow = (d) => (Array.isArray(d) ? { immediate: d.map((t) => ({ what: t })) } : d || {});

export function DoNow({ doNow, practiceIssues = [] }) {
  const d = legacyDoNow(doNow);
  const has = [d.immediate, d.following, d.avoid, d.monitoring, practiceIssues].some((x) => x?.length) || d.reassess;
  if (!has) return null;
  return (
    <Block icon={Zap} title="Cosa fare adesso" testid="result-do-now" accent>
      {practiceIssues.length > 0 && (
        <div className="mb-2 space-y-2" data-testid="result-practice-issues">
          <Sub icon={AlertCircle}>Da correggere nella gestione attuale</Sub>
          {practiceIssues.map((p, i) => (
            <div key={i} className="rounded-xl border border-[#D97706]/40 bg-[#D97706]/5 p-3 text-sm">
              <p className="font-semibold text-[#0D2517]">{p.practice}</p>
              {p.problem && <p className="mt-1 text-[#2D3A30]"><span className="font-semibold text-[#B45309]">Perché incide:</span> {p.problem}</p>}
              {p.correction && <p className="mt-1 text-[#2D3A30]"><span className="font-semibold text-[#15803D]">Come correggere:</span> {p.correction}</p>}
            </div>
          ))}
        </div>
      )}
      {d.immediate?.length > 0 && <><Sub>Subito</Sub><ActionList items={d.immediate} prefix="do-now-immediate" /></>}
      {d.following?.length > 0 && <><Sub>Nei giorni e nelle settimane successive</Sub><ActionList items={d.following} prefix="do-now-following" /></>}
      {d.avoid?.length > 0 && <><Sub>Cosa evitare</Sub><List items={d.avoid} /></>}
      {d.monitoring?.length > 0 && (
        <>
          <Sub>Cosa osservare</Sub>
          <div className="space-y-2">{d.monitoring.map((m, i) => <Fields key={i} item={m} testid={`do-now-monitoring-${i}`} title={<p className="text-sm font-semibold text-[#0D2517]">{m.what}</p>} />)}</div>
        </>
      )}
      {d.improvement_signs?.length > 0 && <><Sub icon={TrendingUp}>Segnali di miglioramento</Sub><List items={d.improvement_signs} /></>}
      {d.worsening_signs?.length > 0 && <><Sub icon={TrendingDown}>Segnali di peggioramento</Sub><List items={d.worsening_signs} /></>}
      <div className="mt-4 space-y-1 text-sm text-[#2D3A30]">
        {d.reassess && <p data-testid="do-now-reassess"><strong>Quando rivalutare:</strong> {d.reassess}</p>}
        {d.escalate && <p data-testid="do-now-escalate"><strong>Quando passare a un intervento successivo:</strong> {d.escalate}</p>}
        {d.professional && <p data-testid="do-now-professional"><strong>Quando chiedere un professionista:</strong> {d.professional}</p>}
      </div>
    </Block>
  );
}

const legacyManagement = (list) => CORE.map((lvl, i) => {
  const items = list.filter((m) => m.level === lvl && m.text).map((m) => (lvl === "biologico" ? { what_to_do: m.text } : { what: m.text }));
  return { level: lvl, n: i + 1, applicable: items.length > 0, items, note: items.length ? "" : "Per questo caso non è indicato un intervento di questo tipo." };
}).concat(list.filter((m) => m.level === "rimedio_domestico").slice(0, 1));

function MgmtPoint({ m }) {
  const bio = m.level === "biologico";
  return (
    <div className="border-l-2 border-[#C9A96E] pl-3" data-testid={`management-${m.level}`}>
      <Tag cls={MGMT[m.level].cls}>{m.n} · {MGMT[m.level].label}</Tag>
      {!m.applicable && <p className="mt-2 text-sm italic text-[#5b6b60]" data-testid={`management-${m.level}-na`}>{m.note}</p>}
      {m.applicable && m.note && <p className="mt-2 text-sm text-[#2D3A30]">{m.note}</p>}
      <div className="mt-2 space-y-2">
        {m.items.map((it, i) => (
          <Fields key={i} item={it} testid={`management-${m.level}-item-${i}`} title={bio ? (
            <p className="text-sm font-semibold text-[#0D2517]"><span className="italic">{it.organism}</span> {ORIGIN[it.origin] && <Tag cls="ml-1 bg-[#15803D]/10 text-[#15803D]">{ORIGIN[it.origin]}</Tag>}</p>
          ) : it.what && <p className="text-sm font-semibold text-[#0D2517]">{it.what}</p>} />
        ))}
      </div>
      {m.favoring_conditions?.length > 0 && <div className="mt-2"><p className="text-xs font-semibold text-[#5b6b60]">Condizioni che favoriscono il problema</p><List items={m.favoring_conditions} /></div>}
      {m.intervene_when && <p className="mt-2 text-sm"><strong>Quando intervenire:</strong> {m.intervene_when}</p>}
    </div>
  );
}

export function Management({ management }) {
  const list = management?.some((m) => "text" in m && !("items" in m)) ? legacyManagement(management) : management?.length ? management : legacyManagement([]);
  const remedy = list.find((m) => m.level === "rimedio_domestico");
  return (
    <Block icon={ListChecks} title="Gestione integrata" testid="result-management">
      <div className="space-y-5">
        {list.filter((m) => CORE.includes(m.level)).map((m) => <MgmtPoint key={m.level} m={m} />)}
        {remedy && (
          <div className="border-l-2 border-[#6D28D9] pl-3" data-testid="management-rimedio_domestico">
            <Tag cls="bg-[#6D28D9] text-white">Rimedio domestico o tradizionale (facoltativo)</Tag>
            <p className="mt-1 text-sm text-[#2D3A30]">{remedy.text}</p>
            <p className="text-[11px] text-[#6D28D9]">Non è un trattamento agronomico comprovato. {remedy.evidence_note}</p>
          </div>
        )}
      </div>
    </Block>
  );
}

export function AnamnesisFactors({ factors }) {
  if (!factors?.length) return null;
  return (
    <Block icon={ClipboardList} title="Come ho usato le tue informazioni" testid="result-anamnesis-factors">
      <div className="space-y-2">
        {factors.map((f, i) => (
          <p key={i} className="text-sm text-[#2D3A30]"><strong>{f.factor}</strong>{f.effect && ` → ${f.effect}`}</p>
        ))}
      </div>
    </Block>
  );
}
